/*
 * Product specs — the swirl turns as the section scrolls past.
 *
 * Scroll-LINKED, not a looping animation: the angle is a pure function of where
 * the section sits in the viewport, so scrolling back up turns it back, stopping
 * scrolling stops it, and there is no timer running while nobody is looking.
 *
 * One full turn across the whole pass — from the section's top entering at the
 * bottom of the screen to its bottom leaving at the top.
 *
 * Deliberately JavaScript rather than CSS `animation-timeline: view()`, which
 * would need no script at all but is still not in every browser this store's
 * customers use; this works everywhere and costs one measure per frame, only
 * while the section is on screen.
 */

const PS_SWIRL_TURN = 360;

if (!customElements.get('product-specs-swirl')) {
  class ProductSpecsSwirl extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.ps') || this;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = () => this.request();
      this.onReduceChange = () => this.update();

      /* Listen to scroll only while the section is actually on screen; the rest
         of the page scrolls for free. */
      this.observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible === this.visible) return;
        this.visible = visible;

        if (visible) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.request();
        } else {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      });
      this.observer.observe(this.section);

      if (this.reduceMotion.addEventListener) {
        this.reduceMotion.addEventListener('change', this.onReduceChange);
      }

      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.reduceMotion?.removeEventListener?.('change', this.onReduceChange);
      cancelAnimationFrame(this.frame);
      this.visible = false;
    }

    /* Scroll fires many times a frame; measure at most once per frame. */
    request() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      if (this.reduceMotion.matches) {
        this.style.removeProperty('--ps-swirl-turn');
        return;
      }

      const box = this.section.getBoundingClientRect();
      const viewport = window.innerHeight;
      const travel = viewport + box.height;
      if (travel <= 0) return;

      const progress = Math.min(1, Math.max(0, (viewport - box.top) / travel));
      this.style.setProperty('--ps-swirl-turn', (progress * PS_SWIRL_TURN).toFixed(2) + 'deg');
    }
  }

  customElements.define('product-specs-swirl', ProductSpecsSwirl);
}
